import React, { useMemo, useState, useEffect, useRef, useTransition, useDeferredValue } from 'react';
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  FilterFn,
  Row,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  CalendarDaysIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from './ui/Tooltip';

// =============================================================================
// ExportsBoardPanel
// =============================================================================
// The Exports workspace "booking board" — the large grouped table ported from
// the legacy CSR dashboard (cms/dashboard_csr.pl dash_CSR_exports, the block
// above the Summary grid). The backend (/lc-dashboards/exports-board) returns one
// row per booking key (bol·orig·dest·term·size·type·date) with seven pre-summed
// Booking-Status counts plus coloring inputs; this panel renders them under two
// header bands — Booking Information and Booking Status.
//
// The board is ALWAYS grouped: initially by the active "Show By" date column, and
// by whatever column header you click. Header clicks cycle the group/sort the way
// our tables do — ascending, then descending, then back to the default date group
// (it never goes ungrouped). Groups are collapsible (expand/collapse-all in the
// toolbar; all expanded by default) and carry a Booking-Status subtotal; a sticky
// footer carries the page grand total. "Hide Completed" drops fully-terminated rows.
//
// Rows are virtualized (the board can run a couple thousand rows) and the table uses
// a fixed layout so columns don't reflow as rows scroll / show / hide.
//
// Every numeric cell drills into the search results for exactly its PROs (row,
// group subtotal, or grand total); every booking-info cell drills into the subset
// sharing that value (e.g. all pickups at BPT, all SCAC MAEU). Row/cell colors port
// the legacy board: red = not available / past-due date, orange = tomorrow / hazmat,
// yellow = two days out, blue = reefer, black = multi-destination, gray = completed.

export interface ExportsBoardRow {
  rowid: string;
  bol: string;
  origin: string;
  destination: string;
  term: string;
  size: string;
  type: string;
  scac: string;
  ssline: string;
  vessel: string;
  firstRec: string;
  firstRecCmp: string;
  docCutoff: string;
  docCutoffCmp: string;
  deliverBy: string;
  cutoff: string;
  dateColor: '' | 'red' | 'orange' | 'yellow';
  isPool: boolean;
  multiDest: boolean;
  reefer: boolean;
  hazmat: string;     // 'Y' | 'N'
  hot: string;        // 'Y' | 'N'
  total: number;
  notDel: number;
  dropd: number;
  loaded: number;
  term_count: number;
  notAvail: number;
  availNotSch: number;
}

export interface ExportsBoardData {
  rows: ExportsBoardRow[];
  group_by: 'cutoff' | 'deliverby';
}

// A clicked cell resolves to a set of booking-key rowids, optionally narrowed to one
// Booking-Status column, plus a ready-made results-header title.
export interface ExportsBoardDrill {
  rowids: string[];
  status?: string;
  title: string;
}

export interface ExportsBoardPanelProps {
  data: ExportsBoardData | null;
  loading: boolean;
  groupBy: 'cutoff' | 'deliverby';
  onGroupByChange: (groupBy: 'cutoff' | 'deliverby') => void;
  onDrill: (drill: ExportsBoardDrill) => void;
}

// The seven Booking-Status columns. `key` is the row/column field; `drill` is the
// backend status key the drill-down filters on.
const STATUS_KEYS: { key: keyof ExportsBoardRow; label: string; drill: string }[] = [
  { key: 'total', label: 'Total', drill: 'total' },
  { key: 'notDel', label: 'Not Del', drill: 'notDel' },
  { key: 'dropd', label: "Drop'd", drill: 'dropd' },
  { key: 'loaded', label: 'Loaded', drill: 'loaded' },
  { key: 'term_count', label: 'Term', drill: 'term' },
  { key: 'notAvail', label: 'Not Avail', drill: 'notAvail' },
  { key: 'availNotSch', label: 'Avail Not Sch', drill: 'availNotSch' },
];
const STATUS_IDS = new Set(STATUS_KEYS.map((s) => s.key as string));

// Booking-info columns, with the label used in drill titles.
const FIELD_LABELS: Record<string, string> = {
  bol: 'Booking #', origin: 'Origin', destination: 'Destination', term: 'Term',
  size: 'Size', type: 'Type', scac: 'SCAC', vessel: 'Vessel', firstRec: '1st Rec',
  docCutoff: 'Doc C/O', cutoff: 'Cutoff', deliverBy: 'Delivery By',
};

// Fixed column widths (px) so the table layout never reflows as rows scroll/toggle.
const COL_WIDTHS: Record<string, number> = {
  bol: 120, origin: 100, destination: 150, term: 120, size: 48, type: 90, scac: 64,
  vessel: 100, firstRec: 60, docCutoff: 92, cutoff: 96, deliverBy: 80,
  total: 56, notDel: 60, dropd: 60, loaded: 64, term_count: 56, notAvail: 50, availNotSch: 56,
};

const ROW_HEIGHT = 28; // single-line row estimate for the virtualizer

// Header cells draw their column separator / band divider via inset box-shadow (see
// HEADER_SHADOW) rather than collapsed borders: under a sticky <thead> + border-collapse
// the collapsed border lines are owned by the table (not the sticky cells), so they don't
// move with the header and rows show through them. An inset shadow is painted as part of
// the cell, so it stays put and opaque.
const HEADER_CELL = 'px-2 py-1 font-semibold select-none whitespace-nowrap overflow-hidden text-ellipsis';
// Wrapping variant for the narrow Booking-Status headers (e.g. "Avail Not Sch"), so
// those columns can be tight and leave more room for the Booking-Information columns.
const HEADER_CELL_WRAP = 'px-1 py-1 font-semibold select-none whitespace-normal break-words leading-tight';
// Inset box-shadow border colors (hex so they compose in one box-shadow string).
const SHADOW_SEP = '#64748b';      // slate-500 — column separator
const SHADOW_DIV_INFO = '#7dd3fc'; // sky-300 — Booking Information (blue) band divider
const SHADOW_DIV_STATUS = '#6ee7b7'; // emerald-300 — Booking Status (green) band divider
// Build a th's inset-shadow border: a left column separator, plus (band-header row only)
// a 2px bottom divider in the band's accent color.
const headerShadow = (bandDividerHex?: string): string => {
  const parts = [`inset 1px 0 0 0 ${SHADOW_SEP}`];
  if (bandDividerHex) parts.push(`inset 0 -2px 0 0 ${bandDividerHex}`);
  return parts.join(', ');
};
// Sticky footer (grand-total) shares the same border-bleed problem: a top rule (to
// separate it from the body) plus the column separator, both as inset shadows.
const FOOTER_SHADOW = `inset 0 2px 0 0 ${SHADOW_SEP}, inset 1px 0 0 0 ${SHADOW_SEP}`;
const TXT_CELL = 'px-2 py-1 border-l border-gray-100 dark:border-gray-700/50 whitespace-nowrap overflow-hidden text-ellipsis';
const NUM_CELL = 'px-2 py-1 border-l border-gray-100 dark:border-gray-700/50 text-right tabular-nums';

// Color classes for the legacy cell/row palette.
const COLOR_CLASS: Record<string, string> = {
  red: 'bg-red-600 text-white',
  orange: 'bg-orange-400 text-black',
  yellow: 'bg-yellow-300 text-black',
  blue: 'bg-blue-500 text-white',
  black: 'bg-black text-white',
  gray: 'bg-[#CCCCCC] text-gray-900',
};

const sortArrow = (dir: false | 'asc' | 'desc') => (dir === 'asc' ? ' ▲' : dir === 'desc' ? ' ▼' : '');
const isCompleted = (r: ExportsBoardRow) => r.total > 0 && r.term_count === r.total;

// A clipped text cell that reveals the full value in the shared app Tooltip when (and
// only when) it's actually truncated — mirrors TanTable's TruncatedCell so the board
// matches the rest of the app. `tooltip` overrides the hover content (e.g. SCAC shows
// the carrier name). Only the Booking-Information columns use this; the numeric status
// counts never overflow.
const TruncatedCell: React.FC<{ text: string; tooltip?: string; className?: string }> = ({ text, tooltip, className }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const check = () => { if (ref.current) setTruncated(ref.current.scrollWidth > ref.current.clientWidth); };
    const id = window.setTimeout(check, 50);
    window.addEventListener('resize', check);
    return () => { window.clearTimeout(id); window.removeEventListener('resize', check); };
  }, [text]);
  const content = tooltip || text;
  const inner = <span ref={ref} className={`block truncate ${className || ''}`}>{text}</span>;
  return truncated && content ? <Tooltip content={content} placement="top">{inner}</Tooltip> : inner;
};

// Column filter: keep rows whose value is one of the selected values. An empty array
// means the user unchecked everything → no rows; a column with no entry in the filter
// state isn't filtered at all (all shown).
const inSelected: FilterFn<ExportsBoardRow> = (row, columnId, filterValue: string[]) =>
  filterValue.includes(String(row.getValue(columnId) ?? ''));

// Faceted multi-select dropdown for one Booking-Information column — lists the distinct
// values present (with counts, like the results-table filters) and filters to the
// checked ones. `selected === undefined` means no filter (everything checked).
const ColumnFacetFilter: React.FC<{
  label: string;
  column: Column<ExportsBoardRow, unknown>;
  selected: string[] | undefined;
  onChange: (next: string[] | undefined) => void;
}> = ({ label, column, selected, onChange }) => {
  const [q, setQ] = useState('');
  const facet = column.getFacetedUniqueValues();
  const values = useMemo(
    () => Array.from(facet.entries())
      .map(([v, count]) => ({ value: String(v ?? ''), count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)),
    [facet],
  );
  const total = values.length;
  const active = selected !== undefined;
  const isChecked = (val: string) => selected === undefined || selected.includes(val);
  const toggle = (val: string) => {
    if (selected === undefined) onChange(values.map((v) => v.value).filter((v) => v !== val));
    else if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else { const next = [...selected, val]; onChange(next.length === total ? undefined : next); }
  };
  const shown = q ? values.filter((v) => v.value.toLowerCase().includes(q.toLowerCase())) : values;
  return (
    <Popover className="relative">
      <PopoverButton
        className={`w-full flex items-center justify-between gap-1 text-xs px-1 py-0.5 rounded border ${active ? 'border-blue-300 bg-blue-500/40 text-white' : 'border-slate-400 bg-slate-700 text-gray-200'}`}
        title={`Filter ${label}`}
      >
        <span className="truncate">{active ? `${selected!.length}/${total}` : 'All'}</span>
        <span aria-hidden>▾</span>
      </PopoverButton>
      <PopoverPanel
        anchor="bottom start"
        className="z-[9999] w-56 rounded border border-gray-300 dark:border-gray-600 bg-panel shadow-lg text-xs text-body"
      >
        <div className="p-1.5 border-b border-gray-200 dark:border-gray-700">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${label}…`}
            className="w-full px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="flex justify-between mt-1.5">
            <button type="button" onClick={() => onChange(undefined)} className="text-blue-600 dark:text-blue-400 hover:underline">Select all</button>
            <button type="button" onClick={() => onChange([])} className="text-blue-600 dark:text-blue-400 hover:underline">Clear</button>
          </div>
        </div>
        <div className="max-h-60 overflow-auto py-1">
          {shown.map((v) => (
            <label key={v.value} className="flex items-center gap-2 px-2 py-0.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
              <input type="checkbox" checked={isChecked(v.value)} onChange={() => toggle(v.value)} className="flex-shrink-0" />
              <span className="flex-1 truncate">{v.value || '(blank)'}</span>
              <span className="text-gray-400 flex-shrink-0">{v.count}</span>
            </label>
          ))}
          {shown.length === 0 && <div className="px-2 py-2 text-gray-400">No values</div>}
        </div>
      </PopoverPanel>
    </Popover>
  );
};

const ExportsBoardPanel: React.FC<ExportsBoardPanelProps> = ({ data, loading, groupBy, onGroupByChange, onDrill }) => {
  const dateColId = groupBy === 'cutoff' ? 'cutoff' : 'deliverBy';

  const [showCompleted, setShowCompleted] = useState(true);
  // Single active group/sort column — the board is never ungrouped. Defaults to the
  // active date column; a header click cycles asc → desc → back to the date default.
  const [sort, setSort] = useState<{ id: string; desc: boolean }>({ id: dateColId, desc: false });
  const [expanded, setExpanded] = useState<ExpandedState>(true); // all groups open by default
  const scrollRef = useRef<HTMLDivElement>(null);
  // Grouping/sorting/hiding all change the table data, which forces react-table to
  // rebuild its grouped+sorted+aggregated model for the whole board (~2k rows). Mark
  // those updates as non-urgent so the rebuild can't block the UI (no click freeze).
  const [isPending, startTransition] = useTransition();

  // Global search + per-column filters. The input values are immediate (responsive
  // typing); the values fed to react-table are deferred so the model rebuild they
  // trigger can't block keystrokes.
  const [search, setSearch] = useState('');
  // Per-column selected values. A column absent from the map is unfiltered; an entry is
  // the explicit set of values to keep ([] = none).
  const [colSel, setColSel] = useState<Record<string, string[]>>({});
  const [showFilters, setShowFilters] = useState(false);
  const deferredSearch = useDeferredValue(search);
  const columnFilters = useMemo<ColumnFiltersState>(
    () => Object.entries(colSel).map(([id, value]) => ({ id, value })),
    [colSel],
  );
  const deferredColumnFilters = useDeferredValue(columnFilters);
  const activeFilterCount = Object.keys(colSel).length + (search.trim() ? 1 : 0);
  const clearFilters = () => { setSearch(''); setColSel({}); };
  const setColumnSelection = (colId: string, next: string[] | undefined) =>
    setColSel((prev) => {
      const n = { ...prev };
      if (next === undefined) delete n[colId]; else n[colId] = next;
      return n;
    });

  // The Show By toggle swaps the date column; re-anchor the group/sort to it and
  // reopen all groups.
  useEffect(() => {
    setSort({ id: dateColId, desc: false });
    setExpanded(true);
  }, [dateColId]);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return showCompleted ? all : all.filter((r) => !isCompleted(r));
  }, [data, showCompleted]);

  const onHeaderClick = (colId: string) => {
    startTransition(() => {
      setSort((prev) => {
        if (prev.id !== colId) return { id: colId, desc: false };     // group asc by new column
        if (!prev.desc) return { id: colId, desc: true };             // flip to desc
        return { id: dateColId, desc: false };                        // third click: back to date default
      });
      setExpanded(true);
    });
  };

  const columns = useMemo<ColumnDef<ExportsBoardRow>[]>(() => {
    const dateCol: ColumnDef<ExportsBoardRow> = groupBy === 'cutoff'
      ? { id: 'cutoff', accessorKey: 'cutoff', header: 'Cutoff', filterFn: inSelected, cell: (c) => c.getValue<string>() }
      : { id: 'deliverBy', accessorKey: 'deliverBy', header: 'Delivery By', filterFn: inSelected, cell: (c) => c.getValue<string>() };

    const text = (id: string, header: string, title?: (r: ExportsBoardRow) => string): ColumnDef<ExportsBoardRow> => ({
      id,
      accessorKey: id,
      header,
      filterFn: inSelected,
      cell: (c) => <span title={title ? title(c.row.original) : undefined}>{c.getValue<string>()}</span>,
    });

    const status = (key: keyof ExportsBoardRow, header: string): ColumnDef<ExportsBoardRow> => ({
      id: key as string,
      accessorKey: key as string,
      header,
      aggregationFn: 'sum',
      enableColumnFilter: false,   // status counts aren't filter targets
      enableGlobalFilter: false,
      cell: (c) => c.getValue<number>(),
      aggregatedCell: (c) => <span className="font-semibold">{c.getValue<number>()}</span>,
    });

    return [
      {
        id: 'booking-info',
        header: 'Booking Information',
        columns: [
          text('bol', 'Booking #'),
          text('origin', 'Origin'),
          text('destination', 'Destination'),
          text('term', 'Term'),
          text('size', 'Size'),
          text('type', 'Type'),
          text('scac', 'SCAC', (r) => r.ssline),
          text('vessel', 'Vessel'),
          text('firstRec', '1st Rec'),
          text('docCutoff', 'Doc C/O'),
          dateCol,
        ],
      },
      {
        id: 'booking-status',
        header: 'Booking Status',
        columns: STATUS_KEYS.map((s) => status(s.key, s.label)),
      },
    ];
  }, [groupBy]);

  // Stable array identities so unrelated re-renders (e.g. virtualizer scroll updates)
  // never look like a grouping/sorting change and trigger a model rebuild.
  const sorting = useMemo(() => [sort], [sort]);
  const grouping = useMemo(() => [sort.id], [sort.id]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, grouping, expanded, globalFilter: deferredSearch, columnFilters: deferredColumnFilters },
    onExpandedChange: setExpanded,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
    getRowId: (r) => r.rowid,
  });

  const leafColumns = table.getVisibleLeafColumns();
  const statusLeafCols = useMemo(() => leafColumns.filter((c) => STATUS_IDS.has(c.id)), [leafColumns]);
  const bookingInfoCount = leafColumns.length - statusLeafCols.length;
  // Widen whichever column is the active group so its header text stays fully readable
  // (e.g. a 48px Size column would otherwise clip to "Siz…").
  const colWidth = (id: string) => (id === sort.id ? Math.max(COL_WIDTHS[id] || 80, 110) : (COL_WIDTHS[id] || 80));
  const tableMinWidth = useMemo(() => leafColumns.reduce((sum, c) => sum + colWidth(c.id), 0), [leafColumns, sort.id]);

  const modelRows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: modelRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  // Leaf rows after global + column filters (pre-grouping), used for the grand total,
  // the booking count, and the grand/field drills so they all reflect what's shown.
  const filteredRows = table.getFilteredRowModel().rows;

  // Page-level grand total across the filtered rows (independent of grouping).
  const grandTotals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const s of STATUS_KEYS) t[s.key as string] = 0;
    for (const row of filteredRows) for (const s of STATUS_KEYS) t[s.key as string] += (row.original[s.key] as number) || 0;
    return t;
  }, [filteredRows]);

  // Row background (legacy precedence): not-available red wins, then completed gray.
  // In Cutoff view a POOL cutoff suppresses the full-row red (only the Not Avail cell
  // highlights); in Delivery view any not-available container reds the whole row.
  const rowBg = (r: ExportsBoardRow): string => {
    const notAvail = groupBy === 'deliverby' ? r.notAvail > 0 : (r.notAvail > 0 && !r.isPool);
    if (notAvail) return COLOR_CLASS.red;
    if (isCompleted(r)) return COLOR_CLASS.gray;
    return '';
  };

  // Per-cell color (overrides the row background).
  const cellColor = (colId: string, r: ExportsBoardRow): string => {
    if (colId === 'bol') return r.hazmat === 'Y' ? COLOR_CLASS.orange : '';
    if (colId === 'destination') return r.multiDest ? COLOR_CLASS.black : (r.hot === 'Y' ? COLOR_CLASS.red : '');
    if (colId === 'type') return r.reefer ? COLOR_CLASS.blue : '';
    if (colId === dateColId) return r.dateColor ? COLOR_CLASS[r.dateColor] : '';
    if (colId === 'notAvail') return r.notAvail > 0 ? COLOR_CLASS.red : '';
    if (colId === 'availNotSch') return r.availNotSch > 0 ? COLOR_CLASS.red : '';
    return '';
  };

  // Drill helpers ------------------------------------------------------------
  const drillRowCount = (r: ExportsBoardRow, s: typeof STATUS_KEYS[number]) => {
    onDrill({ rowids: [r.rowid], status: s.drill, title: `${r.bol || r.origin || 'Booking'} · ${s.label}` });
  };
  const drillField = (colId: string, r: ExportsBoardRow) => {
    const value = String((r as any)[colId] ?? '');
    // Match within the currently-filtered rows so the drill reflects what's on screen.
    const ids = filteredRows
      .filter((x) => String((x.original as any)[colId] ?? '') === value)
      .map((x) => x.original.rowid);
    onDrill({ rowids: ids, title: `${FIELD_LABELS[colId] || colId}: ${value || 'BLANK'}` });
  };
  const drillGroup = (leaf: ExportsBoardRow[], groupLabel: string, s: typeof STATUS_KEYS[number]) => {
    onDrill({ rowids: leaf.map((r) => r.rowid), status: s.drill, title: `${groupLabel} · ${s.label}` });
  };
  const drillGrand = (s: typeof STATUS_KEYS[number]) => {
    onDrill({ rowids: filteredRows.map((row) => row.original.rowid), status: s.drill, title: `All · ${s.label}` });
  };

  // Render one model row (group header or leaf) as a <tr>.
  const renderRow = (row: Row<ExportsBoardRow>) => {
    if (row.getIsGrouped()) {
      const value = String(row.getValue(sort.id) ?? '') || 'BLANK';
      const fieldLabel = FIELD_LABELS[sort.id] || sort.id;
      const groupTitle = `${fieldLabel}: ${value}`;
      const leaf = row.getLeafRows().map((lr) => lr.original);
      return (
        <tr key={row.id} className="bg-slate-100 dark:bg-gray-700/60 font-medium" style={{ height: ROW_HEIGHT }}>
          {/* The grouped value spans the Booking-Information columns (all empty on a
              group row) so it reads fully no matter how narrow the grouped column is. */}
          <td colSpan={bookingInfoCount} className={`${TXT_CELL} cursor-pointer`} onClick={row.getToggleExpandedHandler()}>
            <span className="inline-block w-4 flex-shrink-0 text-gray-500">{row.getIsExpanded() ? '▼' : '▶'}</span>
            <span className="text-gray-500">{fieldLabel}:</span> {value}
            <span className="ml-1 text-gray-400">({row.subRows.length})</span>
          </td>
          {statusLeafCols.map((col) => {
            const s = STATUS_KEYS.find((k) => k.key === col.id)!;
            const v = row.getValue<number>(col.id);
            return (
              <td
                key={col.id}
                className={`${NUM_CELL} ${v > 0 ? 'cursor-pointer hover:underline' : ''}`}
                onClick={v > 0 ? () => drillGroup(leaf, groupTitle, s) : undefined}
              >
                <span className="font-semibold">{v}</span>
              </td>
            );
          })}
        </tr>
      );
    }

    const r = row.original;
    const base = rowBg(r);
    return (
      <tr key={row.id} className={`border-t border-gray-100 dark:border-gray-700/50 ${base || 'hover:bg-blue-50/60 dark:hover:bg-blue-900/20'}`} style={{ height: ROW_HEIGHT }}>
        {row.getVisibleCells().map((cell) => {
          const colId = cell.column.id;
          const color = cellColor(colId, r);
          if (STATUS_IDS.has(colId)) {
            const s = STATUS_KEYS.find((k) => k.key === colId)!;
            const v = cell.getValue<number>();
            return (
              <td
                key={cell.id}
                className={`${NUM_CELL} ${color} ${v > 0 ? 'cursor-pointer hover:underline' : ''}`}
                onClick={v > 0 ? () => drillRowCount(r, s) : undefined}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          }
          return (
            <td key={cell.id} className={`${TXT_CELL} ${color} cursor-pointer hover:underline`} onClick={() => drillField(colId, r)}>
              <TruncatedCell text={String(cell.getValue() ?? '')} tooltip={colId === 'scac' ? r.ssline : undefined} />
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="flex flex-col h-full w-full bg-panel text-sm">
      {/* Toolbar — standardized TanTable-style header: title + count on the left,
          search + icon controls on the right. */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-700 dark:bg-slate-800 text-white">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h2 className="text-sm font-bold truncate">
            Bookings
            <span className="text-xs font-normal text-gray-300 ml-2">
              ({filteredRows.length.toLocaleString()})
            </span>
          </h2>
          {(loading || isPending) && <span className="text-xs text-blue-300">Updating…</span>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bookings…"
            className="w-44 px-2 py-0.5 text-sm border border-slate-500 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {/* Show By: toggles the grouping/sort date column (Cutoff ⇄ Delivery By). */}
          <Tooltip content={groupBy === 'cutoff' ? 'Show by Delivery' : 'Show by Cutoff'} placement="top">
            <button
              type="button"
              aria-label="Toggle Show By"
              onClick={() => onGroupByChange(groupBy === 'cutoff' ? 'deliverby' : 'cutoff')}
              className="p-1 rounded hover:bg-slate-600 dark:hover:bg-slate-900 transition-colors"
            >
              <CalendarDaysIcon className="h-4 w-4 text-white" />
            </button>
          </Tooltip>
          {/* Show / Hide Completed (fully-terminated rows). */}
          <Tooltip content={showCompleted ? 'Hide Completed' : 'Show Completed'} placement="top">
            <button
              type="button"
              aria-label="Toggle Completed"
              onClick={() => startTransition(() => setShowCompleted((v) => !v))}
              className={`p-1 rounded transition-colors ${!showCompleted ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'}`}
            >
              {showCompleted
                ? <EyeSlashIcon className="h-4 w-4 text-white" />
                : <EyeIcon className="h-4 w-4 text-white" />}
            </button>
          </Tooltip>
          {/* Expand all groups. */}
          <Tooltip content="Expand All" placement="top">
            <button
              type="button"
              aria-label="Expand All"
              onClick={() => startTransition(() => setExpanded(true))}
              className="p-1 rounded hover:bg-slate-600 dark:hover:bg-slate-900 transition-colors"
            >
              <ChevronDoubleDownIcon className="h-4 w-4 text-white" />
            </button>
          </Tooltip>
          {/* Collapse all groups. */}
          <Tooltip content="Collapse All" placement="top">
            <button
              type="button"
              aria-label="Collapse All"
              onClick={() => startTransition(() => setExpanded({}))}
              className="p-1 rounded hover:bg-slate-600 dark:hover:bg-slate-900 transition-colors"
            >
              <ChevronDoubleUpIcon className="h-4 w-4 text-white" />
            </button>
          </Tooltip>
          {/* Per-column filters toggle (with active-filter count badge). */}
          <Tooltip content={showFilters ? 'Hide Filters' : 'Filters'} placement="top">
            <button
              type="button"
              aria-label="Toggle Filters"
              onClick={() => setShowFilters((v) => !v)}
              className={`p-1 rounded transition-colors ${showFilters || activeFilterCount ? 'bg-blue-600 dark:bg-blue-700' : 'hover:bg-slate-600 dark:hover:bg-slate-900'}`}
            >
              <span className="relative inline-flex">
                <FunnelIcon className="h-4 w-4 text-white" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[14px] h-3.5 px-0.5 rounded-full bg-amber-500 text-white text-[0.5625rem] font-bold leading-none pointer-events-none">
                    {activeFilterCount}
                  </span>
                )}
              </span>
            </button>
          </Tooltip>
          {/* Clear all filters + search (only when something is active). */}
          {activeFilterCount > 0 && (
            <Tooltip content="Clear Filters" placement="top">
              <button
                type="button"
                aria-label="Clear Filters"
                onClick={clearFilters}
                className="p-1 rounded hover:bg-slate-600 dark:hover:bg-slate-900 transition-colors"
              >
                <XMarkIcon className="h-4 w-4 text-white" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        <table className="border-collapse" style={{ tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth }}>
          <colgroup>
            {leafColumns.map((col) => (
              <col key={col.id} style={{ width: colWidth(col.id) }} />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-10">
            {table.getHeaderGroups().map((hg, gi) => (
              <tr key={hg.id} className="text-white">
                {hg.headers.map((header) => {
                  const isGroupBand = gi === 0 && header.colSpan > 1;
                  const leafId = header.column.id;
                  // Only Booking Information columns group/sort; Booking Status columns
                  // are read-only totals.
                  const sortable = !header.isPlaceholder && !isGroupBand && header.subHeaders.length === 0 && !STATUS_IDS.has(leafId);
                  const sortedDir = sort.id === leafId ? (sort.desc ? 'desc' : 'asc') : false;
                  const wrap = !isGroupBand && STATUS_IDS.has(leafId);
                  // Tint each band differently so Booking Information vs Booking Status
                  // reads at a glance (band header + its subheaders share the hue), and
                  // divide the band header from its subheaders with a bottom rule.
                  const isStatus = isGroupBand ? leafId === 'booking-status' : STATUS_IDS.has(leafId);
                  const bandBg = isStatus ? 'bg-emerald-800 dark:bg-emerald-900' : 'bg-sky-800 dark:bg-sky-900';
                  // Band-header row carries the bottom divider in its accent color.
                  const bandDividerHex = gi === 0 ? (isStatus ? SHADOW_DIV_STATUS : SHADOW_DIV_INFO) : undefined;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${wrap ? HEADER_CELL_WRAP : HEADER_CELL} ${bandBg} ${isGroupBand ? 'text-center' : 'text-left'} ${sortable ? 'cursor-pointer' : ''}`}
                      style={{ boxShadow: headerShadow(bandDividerHex) }}
                      onClick={sortable ? () => onHeaderClick(leafId) : undefined}
                      title={sortable ? 'Click to group & sort; click again to reverse, again to reset' : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortable && sortArrow(sortedDir)}
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
            {showFilters && (
              <tr>
                {leafColumns.map((col) => (
                  <th
                    key={col.id}
                    className={`px-1 py-0.5 align-top ${STATUS_IDS.has(col.id) ? 'bg-emerald-800 dark:bg-emerald-900' : 'bg-sky-800 dark:bg-sky-900'}`}
                    style={{ boxShadow: headerShadow() }}
                  >
                    {STATUS_IDS.has(col.id) ? null : (
                      <ColumnFacetFilter
                        label={FIELD_LABELS[col.id] || col.id}
                        column={col}
                        selected={colSel[col.id]}
                        onChange={(next) => setColumnSelection(col.id, next)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody className="text-body">
            {paddingTop > 0 && <tr style={{ height: paddingTop }}><td colSpan={leafColumns.length} className="p-0" /></tr>}
            {virtualItems.map((vi) => renderRow(modelRows[vi.index]))}
            {paddingBottom > 0 && <tr style={{ height: paddingBottom }}><td colSpan={leafColumns.length} className="p-0" /></tr>}
            {filteredRows.length === 0 && !loading && (
              <tr>
                <td colSpan={leafColumns.length} className="px-2 py-4 text-center text-gray-400">
                  {rows.length === 0 ? 'No export bookings.' : 'No bookings match your filters.'}
                </td>
              </tr>
            )}
          </tbody>
          {filteredRows.length > 0 && (
            <tfoot className="sticky bottom-0">
              <tr className="bg-slate-700 text-white font-semibold">
                {leafColumns.map((col, i) => {
                  if (STATUS_IDS.has(col.id)) {
                    const s = STATUS_KEYS.find((k) => k.key === col.id)!;
                    const v = grandTotals[col.id];
                    return (
                      <td
                        key={col.id}
                        className={`${NUM_CELL} border-0 bg-slate-700 dark:bg-slate-800 ${v > 0 ? 'cursor-pointer hover:underline' : ''}`}
                        style={{ boxShadow: FOOTER_SHADOW }}
                        onClick={v > 0 ? () => drillGrand(s) : undefined}
                      >
                        {v}
                      </td>
                    );
                  }
                  return (
                    <td key={col.id} className={`${TXT_CELL} border-0 bg-slate-700 dark:bg-slate-800`} style={{ boxShadow: FOOTER_SHADOW }}>
                      {i === 0 ? 'Page Total' : ''}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default ExportsBoardPanel;
